import clsx from 'clsx';
import { Children, forwardRef, type PropsWithChildren } from 'react';

import * as styles from './InputGroup.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { Grid } from '../grid/Grid.js';

const COMPONENT_NAME = 'InputGroup';

/**
 * Props for the InputGroup component.
 * @public
 */
export interface InputGroupProps
  extends PropsWithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {}

/**
 * The `InputGroup` component is the outermost layout component of a field
 * component (for example `TextInput`), providing support for grouping.
 * @public
 */
export const InputGroup = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  InputGroupProps
>((inputGroupProps, ref) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': dataDtComponent = COMPONENT_NAME,
    ...remainingProps
  } = inputGroupProps;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  // If there are no children, we don't want to render the component at all to avoid unnecessary DOM elements.
  if (Children.count(children) === 0) {
    return null;
  }

  return (
    <Grid
      ref={ref}
      gridAutoFlow="column"
      gridAutoRows="max-content"
      justifyContent="start"
      columnGap={2}
      rowGap={4}
      className={clsx(consumerClassName, styles.inputGroup)}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...restProps}
      {...behavioralTrackingProps}
      data-dt-component={dataDtComponent}
    >
      {children}
    </Grid>
  );
});

(InputGroup as typeof InputGroup & { displayName: string }).displayName =
  COMPONENT_NAME;
